import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const ProgramReleases = ({ programId, programOverview = null }) => {
  const { t } = useAppContext();
  const isFeatureFilm = programOverview?.progType === 'Feature Film';
  const [releasesData, setReleasesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // const { selectedResult } = useAppContext(); // Removed context usage

  // Fetch releases data (wrapped in useCallback)
  const fetchReleases = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getReleases(programId); // Use programId prop
      setReleasesData(data || []); // Ensure data is an array
    } catch (err) {
      console.error('Failed to fetch releases data:', err);
      setError(t('programReleases.error'));
      setReleasesData([]); // Set to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [programId]); // Dependency on programId

  // Fetch data on mount or when programId changes
  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  // Format date to be more readable
  // Note: Date-only strings like "2012-06-20" are parsed as UTC midnight,
  // which can shift to the previous day when converted to local time.
  // We use UTC methods to avoid this timezone issue.
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">{t('programReleases.loading')}</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-4">{t('programReleases.error')}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={fetchReleases} // Retry fetch
          >
            {t('programReleases.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!releasesData || releasesData.length === 0) { // Check if releasesData is null or empty
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('programReleases.noReleases')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-auto">
      <div className="min-w-full">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left sticky top-0">
            <tr>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programReleases.columns.country')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programReleases.columns.type')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programReleases.columns.date')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programReleases.columns.medium')}</th>
              {!isFeatureFilm && <th className="px-4 py-2 font-medium text-gray-600">{t('programReleases.columns.seasonYear')}</th>}
              {!isFeatureFilm && <th className="px-4 py-2 font-medium text-gray-600">{t('programReleases.columns.seasonName')}</th>}
              <th className="px-4 py-2 font-medium text-gray-600">{t('programReleases.columns.releaseId')}</th>
            </tr>
          </thead>
          <tbody>
            {releasesData.map((item, index) => (
              <tr key={index} className="border-t border-gray-200">
                <td className="px-4 py-3 text-left">{item.country || '-'}</td>
                <td className="px-4 py-3 text-left">{item.type || '-'}</td>
                <td className="px-4 py-3 text-left">{formatDate(item.date)}</td>
                <td className="px-4 py-3 text-left">{item.medium || '-'}</td>
                {!isFeatureFilm && <td className="px-4 py-3 text-left">{item.seasonYear || '-'}</td>}
                {!isFeatureFilm && <td className="px-4 py-3 text-left">{item.seasonName || '-'}</td>}
                <td className="px-4 py-3 text-left">{item.releaseId || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-between text-xs text-gray-500 px-4 pb-4">
          <div>
            {t('programReleases.showing', { count: releasesData.length })}
          </div>
          <div>
            <button
              className={`px-3 py-1 border border-gray-300 rounded-sm ${releasesData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              onClick={() => exportToCsv(releasesData, `${programId}_releases.csv`)}
              disabled={releasesData.length === 0}
            >
              {t('programReleases.export')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramReleases;
